import { useCallback, useMemo, useRef } from 'react';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  useErpAiKbCandidateQuery,
  useErpAiKbCandidatesQuery,
  useErpAiKbReviewerCandidatesQuery,
  useErpAiKbReviewerMeQuery,
  useErpAiKbReviewersQuery,
  useErpApproveAiKbCandidateMutation,
  useErpMarkAiKbCandidateDuplicateMutation,
  useErpRejectAiKbCandidateMutation,
  useErpSetAiKbReviewersMutation,
  useErpUpdateAiKbCandidateMutation,
} from 'gqlhooks';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast';
import { aiAgentErrorCode } from '../AiAgents/aiAgentPlatformModel';
import {
  AI_KB_BUSY,
  AiKbCandidate,
  aiKbApproveVariables,
  AiKbReviewerCategory,
  AiKbReviewerMe,
  aiKbReviewRefusalKey,
  AiKbReviewerUser,
  AI_KB_QUEUE_PAGE_SIZE,
  IAiKbApproveDraft,
} from './aiKnowledgeModel';

const NO_ROWS: AiKbCandidate[] = [];
const NO_REVIEWERS: AiKbReviewerCategory[] = [];
const NO_USERS: AiKbReviewerUser[] = [];

/**
 * После любого решения по предложению — перечитать очередь и счётчик
 * (меню, вкладка). По имени операции: очередь смонтирована со своими
 * фильтрами, Apollo перезапросит её с ними же; неактивные запросы
 * не уходят.
 */
const REVIEW_REFETCH = ['ErpAiKbCandidates', 'ErpAiKbReviewerMe'];

// ── Кто смотрит ────────────────────────────────────────────────────────────

export interface IAiKbReviewerMeState {
  me: AiKbReviewerMe | null;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * ТЗ 0118 B4 — рецензент ли смотрящий (`aiKbReviewerMe`): категории и
 * сколько ждёт разбора. Любой сотрудник ERP; по нему меню показывает пункт,
 * а вкладка и пункт — счётчик. `skip` — пока роль не известна.
 */
export const useAiKbReviewerMe = (skip = false): IAiKbReviewerMeState => {
  const { data, loading, error, refetch } = useErpAiKbReviewerMeQuery({
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const me = data?.aiKbReviewerMe ?? null;
  const reload = useCallback(() => {
    if (skip) return;
    refetch().catch(() => undefined);
  }, [refetch, skip]);
  return { me, loading: loading && !me, error, refetch: reload };
};

// ── Очередь ────────────────────────────────────────────────────────────────

export interface IAiKbQueueFilter {
  category: string | null;
  kind: string | null;
  /** Уже для сервера: `''` — любой статус. */
  status: string;
  offset: number;
}

export interface IAiKbQueueState {
  rows: AiKbCandidate[];
  total: number;
  loading: boolean;
  fetching: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Очередь предложений (`aiKbCandidates`) по фильтру. Пока сервер отвечает на
 * новый фильтр, на экране прежние строки (`previousData`).
 */
export const useAiKbQueue = ({
  category,
  kind,
  status,
  offset,
}: IAiKbQueueFilter): IAiKbQueueState => {
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useErpAiKbCandidatesQuery({
    variables: {
      category: category || null,
      kind: kind || null,
      status,
      limit: AI_KB_QUEUE_PAGE_SIZE,
      offset,
    },
    fetchPolicy: 'cache-and-network',
  });
  const page = (data ?? previousData)?.aiKbCandidates ?? null;
  const rows = useMemo(() => page?.items ?? NO_ROWS, [page]);
  const reload = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
  return {
    rows,
    total: page?.total ?? 0,
    loading: loading && !page,
    fetching: loading,
    error,
    refetch: reload,
  };
};

export interface IAiKbCandidateState {
  candidate: AiKbCandidate | null;
  loading: boolean;
  error?: ApolloError;
  /** Сервер ответил `null`: нет такого или не ваша категория. */
  missing: boolean;
  refetch: () => void;
}

/** Одно предложение по `?candidate=` — карточка открывается и по ссылке. */
export const useAiKbCandidate = (id: string | null): IAiKbCandidateState => {
  const { data, loading, error, refetch } = useErpAiKbCandidateQuery({
    variables: { id: id as string },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });
  const candidate = data?.aiKbCandidate ?? null;
  const reload = useCallback(() => {
    if (!id) return;
    refetch().catch(() => undefined);
  }, [refetch, id]);
  return {
    candidate,
    loading: Boolean(id) && loading && !data,
    error,
    missing: Boolean(id) && Boolean(data) && !candidate,
    refetch: reload,
  };
};

// ── Решения ────────────────────────────────────────────────────────────────

export type AiKbReviewResult =
  | { ok: true; candidate: AiKbCandidate }
  | { ok: false; code: string; detail: string | null };

const errorDetail = (error: unknown): string | null => {
  if (!(error instanceof ApolloError)) return null;
  const text = ((error.graphQLErrors ?? [])[0]?.message ?? '').trim();
  return text || null;
};

const refusalOf = (error: unknown): AiKbReviewResult => ({
  ok: false,
  code: aiAgentErrorCode(error) ?? 'generic',
  detail: errorDetail(error),
});

export interface IAiKbCandidateEdit {
  claim?: string;
  question?: string;
  category?: string;
  targetHeading?: string;
}

/**
 * ТЗ 0118 B4 — решения по предложению: одобрить (с исправленным правилом,
 * ответом на пробел или заменой строки), отклонить с причиной, дубль,
 * поправить. Отказ возвращается форме карточки (объяснение у кнопки), тост
 * — об успехе. Вторая отправка, пока едет первая, не уходит: одна кнопка —
 * одно решение.
 */
export const useAiKbReviewActions = () => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const inFlight = useRef(false);
  const options = { refetchQueries: REVIEW_REFETCH, awaitRefetchQueries: true };
  const [approveMutation, approveState] = useErpApproveAiKbCandidateMutation(
    options,
  );
  const [rejectMutation, rejectState] = useErpRejectAiKbCandidateMutation(
    options,
  );
  const [
    duplicateMutation,
    duplicateState,
  ] = useErpMarkAiKbCandidateDuplicateMutation(options);
  const [updateMutation, updateState] = useErpUpdateAiKbCandidateMutation();

  const success = useCallback(
    (title: string) =>
      addToast({
        type: GlobalToastTypeNames.AiAgents,
        data: { type: ToastTypes.success, title },
      }),
    [addToast],
  );

  const run = useCallback(
    async (
      call: () => Promise<AiKbCandidate | null | undefined>,
      toastOf: ((candidate: AiKbCandidate) => string) | null,
    ): Promise<AiKbReviewResult> => {
      if (inFlight.current) {
        return { ok: false, code: AI_KB_BUSY, detail: null };
      }
      inFlight.current = true;
      try {
        const candidate = await call();
        if (!candidate) return { ok: false, code: 'generic', detail: null };
        if (toastOf) success(toastOf(candidate));
        return { ok: true, candidate };
      } catch (error) {
        return refusalOf(error);
      } finally {
        inFlight.current = false;
      }
    },
    [success],
  );

  const approve = useCallback(
    (candidate: AiKbCandidate, draft: IAiKbApproveDraft) =>
      run(
        async () =>
          (
            await approveMutation({
              variables: aiKbApproveVariables(candidate, draft),
            })
          ).data?.approveAiKbCandidate,
        (done) =>
          t('aiKb.review.toast.approved', {
            title: done.targetDocumentTitle || '—',
            version: done.appliedRevisionVersion ?? '—',
          }),
      ),
    [run, approveMutation, t],
  );

  const reject = useCallback(
    (id: string, reason: string) =>
      run(
        async () =>
          (await rejectMutation({ variables: { id, reason: reason.trim() } }))
            .data?.rejectAiKbCandidate,
        () => t('aiKb.review.toast.rejected'),
      ),
    [run, rejectMutation, t],
  );

  const markDuplicate = useCallback(
    (id: string, duplicateOfId: string | null) =>
      run(
        async () =>
          (
            await duplicateMutation({
              variables: { id, duplicateOfId: duplicateOfId || null },
            })
          ).data?.markAiKbCandidateDuplicate,
        () => t('aiKb.review.toast.duplicate'),
      ),
    [run, duplicateMutation, t],
  );

  /**
   * Поправить предложение. `silent` — шаг перед одобрением («Zatwierdź» с
   * исправленной категорией): тост будет один, об одобрении.
   */
  const update = useCallback(
    (id: string, edit: IAiKbCandidateEdit, silent = false) =>
      run(
        async () =>
          (
            await updateMutation({
              variables: {
                id,
                claim: edit.claim ?? null,
                question: edit.question ?? null,
                category: edit.category ?? null,
                targetHeading: edit.targetHeading ?? null,
              },
            })
          ).data?.updateAiKbCandidate,
        silent ? null : () => t('aiKb.review.toast.saved'),
      ),
    [run, updateMutation, t],
  );

  return {
    approve,
    reject,
    markDuplicate,
    update,
    busy:
      approveState.loading ||
      rejectState.loading ||
      duplicateState.loading ||
      updateState.loading,
  };
};

// ── Рецензенты («Настройки → ИИ») ──────────────────────────────────────────

export interface IAiKbReviewersState {
  reviewers: AiKbReviewerCategory[];
  people: AiKbReviewerUser[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
  /** Заменить список рецензентов категории; `true` — сохранено. */
  save: (category: string, userIds: string[]) => Promise<boolean>;
  saving: boolean;
}

/**
 * ТЗ 0118 B4 — рецензенты базы знаний по категориям (Admin): кто сейчас
 * назначен (`aiKbReviewers`) и из кого выбирать (`aiKbReviewerCandidates`).
 * Сохранение заменяет список категории целиком; ответ ложится в кеш по
 * категории — перечитывать нечего, кроме своего счётчика.
 */
export const useAiKbReviewers = (): IAiKbReviewersState => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const reviewersQuery = useErpAiKbReviewersQuery({
    fetchPolicy: 'cache-and-network',
  });
  const peopleQuery = useErpAiKbReviewerCandidatesQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [setMutation, setState] = useErpSetAiKbReviewersMutation({
    refetchQueries: ['ErpAiKbReviewers', 'ErpAiKbReviewerMe'],
    awaitRefetchQueries: true,
  });

  const reviewers = reviewersQuery.data?.aiKbReviewers ?? NO_REVIEWERS;
  const people = peopleQuery.data?.aiKbReviewerCandidates ?? NO_USERS;
  const { refetch: refetchReviewers } = reviewersQuery;
  const { refetch: refetchPeople } = peopleQuery;

  const refetch = useCallback(() => {
    refetchReviewers().catch(() => undefined);
    refetchPeople().catch(() => undefined);
  }, [refetchReviewers, refetchPeople]);

  const save = useCallback(
    async (category: string, userIds: string[]) => {
      try {
        await setMutation({ variables: { category, userIds } });
        addToast({
          type: GlobalToastTypeNames.AiAgents,
          data: {
            type: ToastTypes.success,
            title: t('aiKb.reviewers.toast.saved', {
              category: t(`aiKb.category.${category}`),
            }),
          },
        });
        return true;
      } catch (error) {
        addToast({
          type: GlobalToastTypeNames.AiAgents,
          data: {
            type: ToastTypes.error,
            title: t('aiKb.reviewers.toast.failed'),
            text: t(aiKbReviewRefusalKey(aiAgentErrorCode(error))),
          },
        });
        return false;
      }
    },
    [setMutation, addToast, t],
  );

  return {
    reviewers,
    people,
    loading:
      (reviewersQuery.loading && !reviewersQuery.data) ||
      (peopleQuery.loading && !peopleQuery.data),
    error: reviewersQuery.error ?? peopleQuery.error,
    refetch,
    save,
    saving: setState.loading,
  };
};
